import { SyntheticEvent, useMemo } from 'react';
import { Routes, Navigate, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { Page } from '@occc-app/design-system';
import { Tab, Tabs } from '@occc/ui-design-system';
import { SentryRoute as Route } from '@occc-app/App/sentry';
import { RetailerNetworkFragment } from '@occc/graphql';

import RetailerNetworkConfiguration from '../retailer-network-configuration/retailer-network-configuration.component';
import { RetailerNetworkConfigurationFormValues } from '../retailer-network-configuration/retailer-network-configuration.types';
import {
  RetailerNetworkConfigurationTabs,
  RetailerNetworkUpdateHandler,
} from '../retailer-connect.types';
import { routeCreator, getTabItems } from '../retailer-connect.utils';
import RetailerNetworkUsersConfigurationContainer from '../retailer-network-users-configuration';
import RetailerNetworkLocationsConfigurationContainer from '../retailer-network-locations-configuration';

const getTabsRouteMap = (uuid: string) => ({
  [RetailerNetworkConfigurationTabs.Details]: routeCreator.configurationUpdateNetworkDetails(uuid),
  [RetailerNetworkConfigurationTabs.Locations]:
    routeCreator.configurationUpdateNetworkLocations(uuid),
  [RetailerNetworkConfigurationTabs.Users]: routeCreator.configurationUpdateNetworkUsers(uuid),
});

type RetailerNetworkUpdatePageProps = {
  tabValue: RetailerNetworkConfigurationTabs;
  setTabValue: (value: RetailerNetworkConfigurationTabs) => void;
  retailerNetworkUpdate: RetailerNetworkUpdateHandler;
  retailerNetwork: RetailerNetworkFragment;
  retailerNetworkConfigurationUpdate: (
    values: RetailerNetworkConfigurationFormValues,
  ) => Promise<void>;
};

export const RetailerNetworkUpdatePage = ({
  tabValue,
  setTabValue,
  retailerNetworkUpdate,
  retailerNetwork,
  retailerNetworkConfigurationUpdate,
}: RetailerNetworkUpdatePageProps) => {
  const navigate = useNavigate();
  const tabItems = getTabItems(false);
  const routesMap = useMemo(() => getTabsRouteMap(retailerNetwork.uuid), [retailerNetwork.uuid]);

  const onTabChange = (e: SyntheticEvent, value: RetailerNetworkConfigurationTabs) => {
    setTabValue(value);
    navigate(routesMap[value]);
  };

  return (
    <Page
      browserTabTitle='Update Retailer Network'
      pageHeaderProps={{
        title: retailerNetwork.name,
        subtitle: `Customer Id: ${retailerNetwork.customerId}`,
      }}
      containerProps={{ variant: 'md' }}
    >
      <Box ml={2}>
        <Tabs
          variant='scrollable'
          scrollButtons='auto'
          indicatorColor='secondary'
          textColor='secondary'
          value={tabValue}
          onChange={onTabChange}
        >
          {tabItems.map((item) => (
            <Tab
              key={item.value}
              value={item.value}
              label={item.label}
              disabled={item.disabled || !retailerNetwork}
            />
          ))}
        </Tabs>
      </Box>
      <Routes>
        <Route
          path=''
          element={
            <Navigate to={routeCreator.configurationUpdateNetworkDetails(retailerNetwork.uuid)} />
          }
        />
        <Route
          key='configuration'
          path='details'
          element={
            <RetailerNetworkConfiguration
              key='configuration'
              retailerNetwork={retailerNetwork}
              onSubmit={retailerNetworkConfigurationUpdate}
            />
          }
        />
        <Route
          key='locations'
          path='locations'
          element={
            <RetailerNetworkLocationsConfigurationContainer
              retailerNetworkUuid={retailerNetwork.uuid}
              retailerNetworkUpdate={retailerNetworkUpdate}
              customerId={retailerNetwork.customerId}
            />
          }
        />
        <Route
          key='users'
          path='users'
          element={
            <RetailerNetworkUsersConfigurationContainer
              retailerNetwork={retailerNetwork}
              retailerNetworkUpdate={retailerNetworkUpdate}
              showNavigationButtons
            />
          }
        />
      </Routes>
    </Page>
  );
};
